import { apiErrorSchema } from '@fewerbags/schema';
/** Thrown for every non-2xx response. `code` is the API's machine-readable code when present. */
export class ApiError extends Error {
    status;
    code;
    body;
    constructor(status, message, body, code) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.body = body;
    }
    static fromResponse(method, path, status, body) {
        const parsed = apiErrorSchema.safeParse(body);
        const err = parsed.success ? parsed.data : undefined;
        return new ApiError(status, err?.message ?? `${method} ${path} failed with ${status}`, body, err?.code ?? (status === 401 ? 'AUTH_REQUIRED' : undefined));
    }
}
/** Network failure (fetch rejected) — the server was never reached. */
export class NetworkError extends Error {
    constructor(cause) {
        super('Could not reach the server');
        this.name = 'NetworkError';
        this.cause = cause;
    }
}
export function createHttp(opts) {
    const f = opts.fetch ?? ((...args) => globalThis.fetch(...args));
    const base = opts.baseUrl.replace(/\/$/, '');
    function url(path, query) {
        if (!query)
            return `${base}${path}`;
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== '')
                qs.set(k, String(v));
        }
        const s = qs.toString();
        return s ? `${base}${path}?${s}` : `${base}${path}`;
    }
    /** `{ authorization: 'Bearer <token>' }` when a token provider is configured and returns one. */
    async function authHeaders() {
        if (!opts.getAccessToken)
            return {};
        const token = await opts.getAccessToken();
        return token ? { authorization: `Bearer ${token}` } : {};
    }
    /** Shared response handling (parse body, map errors) for both JSON and form requests. */
    async function handleResponse(method, path, res, o) {
        const text = await res.text();
        let body = undefined;
        if (text) {
            try {
                body = JSON.parse(text);
            }
            catch {
                body = text;
            }
        }
        if (!res.ok) {
            if (res.status === 401 && !o.silent401)
                opts.onUnauthorized?.(path);
            throw ApiError.fromResponse(method, path, res.status, body);
        }
        if (!o.parse)
            return undefined;
        return typeof o.parse === 'function' ? o.parse(body) : o.parse.parse(body);
    }
    async function request(method, path, o = {}) {
        let res;
        try {
            res = await f(url(path, o.query), {
                method,
                credentials: 'include',
                headers: {
                    accept: 'application/json',
                    ...(o.body !== undefined ? { 'content-type': 'application/json' } : {}),
                    ...(await authHeaders()),
                },
                ...(o.body !== undefined ? { body: JSON.stringify(o.body) } : {}),
            });
        }
        catch (cause) {
            throw new NetworkError(cause);
        }
        return handleResponse(method, path, res, o);
    }
    /**
     * POST a `FormData` body (file uploads). No `content-type` header is set —
     * the fetch implementation fills in `multipart/form-data` with its boundary.
     */
    async function requestForm(path, form, o = {}) {
        let res;
        try {
            res = await f(url(path, o.query), {
                method: 'POST',
                credentials: 'include',
                headers: { accept: 'application/json', ...(await authHeaders()) },
                body: form,
            });
        }
        catch (cause) {
            throw new NetworkError(cause);
        }
        return handleResponse('POST', path, res, o);
    }
    return {
        get: (path, o) => request('GET', path, o),
        post: (path, body, o) => request('POST', path, { ...o, ...(body !== undefined ? { body } : {}) }),
        put: (path, body, o) => request('PUT', path, { ...o, ...(body !== undefined ? { body } : {}) }),
        patch: (path, body, o) => request('PATCH', path, { ...o, ...(body !== undefined ? { body } : {}) }),
        del: (path, body, o) => request('DELETE', path, { ...o, ...(body !== undefined ? { body } : {}) }),
        /** POST a FormData body (multipart file upload); response handling matches `post`. */
        postForm: (path, form, o) => requestForm(path, form, o),
    };
}
//# sourceMappingURL=http.js.map